import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhotoboothService } from '../../services/photobooth.service';

@Component({
  selector: 'app-daily-revenue-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './daily-revenue-modal.component.html',
  styleUrls: ['./daily-revenue-modal.component.css']
})
export class DailyRevenueModalComponent implements OnInit {
  @Input() deviceId!: string;
  @Input() deviceName!: string;
  @Output() close = new EventEmitter<void>();

  private photoboothService = inject(PhotoboothService);

  selectedDateStr: string = '';
  isLoading: boolean = false;
  revenueData: any = null;
  monthlyData: any = null;
  hasLoaded: boolean = false;

  ngOnInit(): void {
    // Default to today's date in local time YYYY-MM-DD
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.selectedDateStr = `${yyyy}-${mm}-${dd}`;
    
    this.fetchRevenue();
  }

  async fetchRevenue(): Promise<void> {
    if (!this.selectedDateStr || !this.deviceId) return;
    
    this.isLoading = true;
    this.revenueData = null;
    this.monthlyData = null;
    this.hasLoaded = false;
    
    try {
      // Split the selected date to extract period (YYYY-MM) and date (YYYY-MM-DD)
      const parts = this.selectedDateStr.split('-');
      if (parts.length === 3) {
        const period = `${parts[0]}-${parts[1]}`;
        const date = this.selectedDateStr;
        
        const [dailyRes, monthlyRes] = await Promise.all([
          this.photoboothService.getDailyRevenue(this.deviceId, period, date),
          this.photoboothService.getMonthlyReport(this.deviceId, period)
        ]);

        if (dailyRes) {
          this.revenueData = dailyRes;
        }
        if (monthlyRes) {
          this.monthlyData = monthlyRes;
        }
      }
    } catch (error) {
      console.error('Error fetching daily revenue:', error);
    } finally {
      this.isLoading = false;
      this.hasLoaded = true;
    }
  }

  get totalRevenue(): number {
    if (!this.revenueData) return 0;
    
    // Look for explicit total/revenue fields
    const keysToTry = [
      'dailyRevenue',
      'daily_revenue',
      'totalRevenue', 
      'revenue', 
      'total', 
      'total_revenue', 
      'totalAmount', 
      'total_amount', 
      'grandTotal', 
      'amount'
    ];
    
    for (const key of keysToTry) {
      if (this.revenueData[key] !== undefined && this.revenueData[key] !== null) {
        return Number(this.revenueData[key]);
      }
    }
    
    // Fallback: If cash and card/credit exist, sum them up
    const cash = Number(this.revenueData.cash || this.revenueData.cashRevenue || this.revenueData.totalCash || 0);
    const card = Number(this.revenueData.card || this.revenueData.credit || this.revenueData.cardRevenue || this.revenueData.creditRevenue || this.revenueData.totalCredit || 0);
    if (cash > 0 || card > 0) {
      return cash + card;
    }
    
    return 0;
  }

  get detailFields(): { label: string, value: any }[] {
    if (!this.revenueData) return [];
    const fields: { label: string, value: any }[] = [];
    
    const ignoredKeys = [
      'dailyRevenue', 'daily_revenue',
      'totalRevenue', 'revenue', 'total', 'total_revenue', 
      'totalAmount', 'total_amount', 'grandTotal', 'amount',
      'date', 'deviceId', 'period'
    ];
    for (const key of Object.keys(this.revenueData)) {
      if (!ignoredKeys.includes(key)) {
        // Format label nicely: convert camelCase or snake_case to Title Case
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .trim()
          .replace(/^\w/, (c) => c.toUpperCase());
        fields.push({ label, value: this.revenueData[key] });
      }
    }
    return fields;
  }

  get monthlyTotalRevenue(): number {
    if (!this.monthlyData) return 0;
    return Number(this.monthlyData.TotalRevenue || this.monthlyData.totalRevenue || 0);
  }

  get monthlyTotalSessions(): number {
    if (!this.monthlyData) return 0;
    return Number(this.monthlyData.TotalSession || this.monthlyData.totalSession || 0);
  }
}
